import { compact } from 'lodash';
import { ReactElement, useMemo } from 'react';

import { HealthcareProviderType } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { assertUnreachable, PromiseVoidFunction, TranslatableText } from '@dreryk/react-common';
import { NavTabs } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { DocplannerIntegrationPanel } from '~legacy.docplanner';
import { useInvoiceNavTab } from '~legacy.invoice';
import { useOnlinePaymentsNavTab } from '~legacy.online-payments';
import { HealthcareProviderPatientPortalPanel } from '~legacy.patient-portal';
import { EmergencyNotificationSettingsPanel } from '~libs.desktop.emergency-notification';
import { AiAssistantSettingsPanel, aiAssistantSettingsPanelTranslationKeys } from '~libs.desktop.ai-assistant';

import { HealthcareProviderNavParams } from '../routes';
import {
   communicationWithP1TranslationKeys,
   dataStorageTranslationKeys,
   emergencyNotificationTranslationKeys,
   generalSettingsTranslationKeys,
   healthCareInstitutionTranslationKeys,
   healthcareOrganizationTranslationKeys,
   healthcarePracticeTranslationKeys,
   nfzSettingsTranslationKeys,
   patientPhoneNotificationTranslationKeys,
   practitionersTranslationKeys,
} from '../translations';

import { HealthcareProviderActionBar } from './common/HealthcareProviderActionBar';
import { CommunicationWithP1 } from './communicationWithP1/CommunicationWithP1';
import { DataStoragePanel } from './dataStorage/DataStoragePanel';
import { DataStoragePanelLoader } from './dataStorage/DataStoragePanel.loader';
import { GeneralSettingsPanel } from './generalSettings/GeneralSettingsPanel';
import { HealthcareOrganizationPanel } from './healthcareOrganization/HealthcareOrganizationPanel';
import { HealthcareOrganizationPanelLoader } from './healthcareOrganization/HealthcareOrganizationPanel.loader';
import { HealthcarePracticePanel } from './healthcarePractice/HealthcarePracticePanel';
import { NfzSettingsPanel } from './nfzSettings/NfzSettingsPanel';
import { PatientPhoneNotificationPanel } from './notification/PatientPhoneNotificationPanel';
import { PractitionersTable } from './practitioners/table/PractitionersTable';

type HealthcareProviderTabsOptions = {
   healthcareProviderType: HealthcareProviderType;
   verifyAccountConfiguration: PromiseVoidFunction;
};

type HealthcareProviderNavTab = {
   param: HealthcareProviderNavParams;
   label: TranslatableText;
   content: ReactElement;
};

const resolveHealthcareProviderDataTab = (
   options: HealthcareProviderTabsOptions
): HealthcareProviderNavTab | undefined => {
   switch (options.healthcareProviderType) {
      case 'HEALTHCARE_PRACTICE':
         return {
            param: HealthcareProviderNavParams.HEALTHCARE_PRACTICE_DATA,
            label: healthcarePracticeTranslationKeys.tabName,
            content: (
               <HealthcareProviderActionBar>
                  <HealthcarePracticePanel verifyAccountConfiguration={options.verifyAccountConfiguration} />
               </HealthcareProviderActionBar>
            ),
         };
      case 'HEALTHCARE_ORGANIZATION':
         return {
            param: HealthcareProviderNavParams.HEALTHCARE_ORGANIZATION_DATA,
            label: healthcareOrganizationTranslationKeys.tabName,
            content: (
               <HealthcareProviderActionBar>
                  <HealthcareOrganizationPanelLoader>
                     {({ healthcareOrganization }) => (
                        <HealthcareOrganizationPanel healthcareOrganization={healthcareOrganization} />
                     )}
                  </HealthcareOrganizationPanelLoader>
               </HealthcareProviderActionBar>
            ),
         };
      default:
         return assertUnreachable(options.healthcareProviderType);
   }
};

const useHealthcareProviderTabs = (options: HealthcareProviderTabsOptions): HealthcareProviderNavTab[] => {
   const invoiceNavTab = useInvoiceNavTab({ param: HealthcareProviderNavParams.INVOICES });
   const onlinePaymentsTab = useOnlinePaymentsNavTab({ param: HealthcareProviderNavParams.ONLINE_PAYMENTS });

   return useMemo(
      () =>
         compact([
            resolveHealthcareProviderDataTab({
               healthcareProviderType: options.healthcareProviderType,
               verifyAccountConfiguration: options.verifyAccountConfiguration,
            }),
            {
               param: HealthcareProviderNavParams.COMMUNICATION_WITH_P1,
               label: communicationWithP1TranslationKeys.tabName,
               content: <CommunicationWithP1 verifyAccountConfiguration={options.verifyAccountConfiguration} />,
            },
            {
               param: HealthcareProviderNavParams.NFZ_SETTINGS,
               label: nfzSettingsTranslationKeys.nfzSettings,
               content: <NfzSettingsPanel />,
            },
            {
               param: HealthcareProviderNavParams.GENERAL_SETTINGS,
               label: generalSettingsTranslationKeys.tabName,
               content: <GeneralSettingsPanel />,
            },
            protectFeature(['aiExperimental', 'aiAssistant'], {
               param: HealthcareProviderNavParams.AI_ASSISTANT,
               label: aiAssistantSettingsPanelTranslationKeys.tabName,
               content: <AiAssistantSettingsPanel />,
            }),
            protectFeature('updatingProviderData', {
               param: HealthcareProviderNavParams.EMERGENCY_NOTIFICATION,
               label: emergencyNotificationTranslationKeys.tabName,
               content: <EmergencyNotificationSettingsPanel />,
            }),
            protectFeature('managingDocplannerIntegration', {
               param: HealthcareProviderNavParams.DOCPLANNER,
               label: healthCareInstitutionTranslationKeys.docplanner.tabName,
               content: <DocplannerIntegrationPanel />,
            }),
            protectFeature('updatingProviderData', {
               param: HealthcareProviderNavParams.DATA_STORAGE,
               label: dataStorageTranslationKeys.tabName,
               content: (
                  <DataStoragePanelLoader>
                     {({ healthcareProviderConstraint }) => (
                        <DataStoragePanel healthcareProviderConstraint={healthcareProviderConstraint} />
                     )}
                  </DataStoragePanelLoader>
               ),
            }),
            {
               param: HealthcareProviderNavParams.PRACTITIONERS,
               label: practitionersTranslationKeys.tabName,
               content: <PractitionersTable />,
            },
            invoiceNavTab,
            onlinePaymentsTab,
            {
               param: HealthcareProviderNavParams.NOTIFICATIONS,
               label: patientPhoneNotificationTranslationKeys.tabName,
               content: <PatientPhoneNotificationPanel />,
            },
            protectFeature('managingPatientPortal', {
               param: HealthcareProviderNavParams.PATIENT_PORTAL,
               label: healthCareInstitutionTranslationKeys.patientPortal.tabName,
               content: <HealthcareProviderPatientPortalPanel />,
            }),
         ]),
      [options.healthcareProviderType, options.verifyAccountConfiguration, invoiceNavTab, onlinePaymentsTab]
   );
};

type HealthcareProviderNavTabsProps = {
   readonly healthcareProviderType: HealthcareProviderType;
   readonly verifyAccountConfiguration: PromiseVoidFunction;
};

export const HealthcareProviderNavTabs = (props: HealthcareProviderNavTabsProps): JSX.Element => {
   const tabs = useHealthcareProviderTabs({
      healthcareProviderType: props.healthcareProviderType,
      verifyAccountConfiguration: props.verifyAccountConfiguration,
   });
   return (
      <NavTabs<HealthcareProviderNavParams> testId={'healthcareProvider'}>
         {tabs.map((tab) => (
            <NavTabs.Tab key={tab.param} testId={tab.param} param={tab.param} label={tab.label}>
               {tab.content}
            </NavTabs.Tab>
         ))}
      </NavTabs>
   );
};
